// C1b Sync scheduler, its decisions (pure, driven by connectivity_edges.json
// and scheduler_backoff.json). src/lib/offline/syncScheduler.ts runs it.
//
// Step 0 of every run is the session refresh: attempted when C1 is online,
// C2 is refresh_pending and the refresh's OWN backoff has elapsed. Only
// `recovered` makes a backed-off refresh due at once. Then the account-owned
// steps (C1 online AND C2 active), then the public steps (C1 online).
// Single flight: a trigger during a run sets `rerun`; one more run follows.
import { MODE, TRIGGER, type IdentityMode, type SyncTrigger } from './offline.codes'

export const BACKOFF_BASE_MS = 2000
export const BACKOFF_CAP_MS = 300_000
export const TICK_MS = 60_000

/**
 * `min(2,000 * 2^(n-1), 300,000) * (0.5 + 0.5 * u)` ms after the n-th
 * consecutive transient / ambiguous outcome, raised to at least Retry-After.
 */
export function backoffDelayMs(n: number, u: number, retryAfterS?: number | null): number {
  const raw = Math.min(BACKOFF_BASE_MS * 2 ** (Math.max(n, 1) - 1), BACKOFF_CAP_MS) * (0.5 + 0.5 * u)
  const floor = (retryAfterS ?? 0) * 1000
  return Math.round(Math.max(raw, floor))
}

export interface RunPlan {
  trigger: SyncTrigger
  /** Step 0: attempt the session refresh. */
  attemptRefresh: boolean
  /** The account-owned steps run (C1 online AND C2 active). */
  drain: boolean
  /** The public steps run (C1 online). */
  publicSteps: boolean
}

export class SchedulerCore {
  running = false
  rerunPending = false
  /** Consecutive transient refresh failures (its own counter). */
  refreshFailures = 0
  /** null: due now. */
  refreshDueAt: number | null = null

  /** Does `trigger` start (or queue) a run? `enqueued` always; the others need pending work or a pending refresh. */
  static admits(trigger: SyncTrigger, pendingWork: boolean, refreshPending: boolean): boolean {
    if (trigger === TRIGGER.enqueued || trigger === TRIGGER.rerun) return true
    return pendingWork || refreshPending
  }

  refreshIsDue(now: number): boolean {
    return this.refreshDueAt === null || now >= this.refreshDueAt
  }

  /** Start a run, or (one is running) set `rerun` and answer null. */
  begin(trigger: SyncTrigger, now: number, online: boolean, mode: IdentityMode): RunPlan | null {
    // Only `recovered` makes a waiting refresh due at once.
    if (trigger === TRIGGER.recovered) this.refreshDueAt = null
    if (this.running) {
      this.rerunPending = true
      return null
    }
    this.running = true
    return {
      trigger,
      attemptRefresh: online && mode === MODE.refreshPending && this.refreshIsDue(now),
      drain: online && mode === MODE.active,
      publicSteps: online,
    }
  }

  refreshFinished(ok: boolean, now: number, u: number): void {
    if (ok) {
      this.refreshFailures = 0
      this.refreshDueAt = null
    } else {
      this.refreshFailures += 1
      this.refreshDueAt = now + backoffDelayMs(this.refreshFailures, u)
    }
  }

  /** The run ended. True: a rerun is owed (start it now). */
  end(): boolean {
    this.running = false
    if (!this.rerunPending) return false
    this.rerunPending = false
    return true
  }
}
